import { useRef, type ComponentType, type KeyboardEvent, type ReactNode, useId } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { Icon } from '../Icon';

export type TabItem = {
  id: string;
  label: ReactNode;
  /** A link tab: the whole bar becomes navigation (aria-current) instead of a tablist. */
  href?: string;
  /** A count after the label, e.g. saved jobs. Formatted by the caller. */
  count?: ReactNode;
  icon?: ComponentType<IconProps>;
};

export type TabsProps = {
  items: TabItem[];
  /** The selected tab's id. */
  value: string;
  /** Called with the new id (tablist mode). */
  onValueChange?: (id: string) => void;
  /** Accessible name of the bar, localised by the caller. */
  label: string;
  /**
   * Prefix for the tab and panel ids. Pass it when each tab controls a panel, and spread
   * tabPanelProps(idPrefix, id) on that panel: only then do tabs point aria-controls at it.
   */
  idPrefix?: string;
  className?: string;
};

/** Props for the panel a tab controls: `<div {...tabPanelProps('hub', 'saved')}>`. */
export function tabPanelProps(idPrefix: string, id: string) {
  return {
    role: 'tabpanel' as const,
    id: `${idPrefix}-panel-${id}`,
    'aria-labelledby': `${idPrefix}-tab-${id}`,
    tabIndex: 0,
  };
}

const tabClass = (selected: boolean) =>
  cx(
    'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-jc-control px-4 text-sm font-semibold whitespace-nowrap',
    'transition-colors duration-(--jc-duration-fast) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
    selected ? 'bg-jc-selected text-jc-on-selected' : 'text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink',
  );

function TabContent({ item }: { item: TabItem }) {
  return (
    <>
      {item.icon ? <Icon icon={item.icon} size="sm" /> : null}
      <span>{item.label}</span>
      {item.count != null ? <span className="text-xs tabular-nums opacity-80">{item.count}</span> : null}
    </>
  );
}

/**
 * A row of tabs that scrolls sideways and never wraps. With hrefs it is page navigation
 * (`nav` + aria-current); without, an ARIA tablist with arrow keys, Home and End.
 */
export function Tabs({ items, value, onValueChange, label, idPrefix, className }: TabsProps) {
  const autoPrefix = `tb${useId().replace(/:/g, '')}`;
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const rowClass = cx(
    'flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-jc-border pb-2',
    className,
  );

  const linked = items.filter((i) => i.href).length;
  devWarn(
    linked > 0 && linked < items.length,
    'Tabs: give every item an href (navigation) or none (a tablist); a mix renders as a tablist.',
  );
  if (items.every((i) => i.href)) {
    return (
      <nav aria-label={label} className={rowClass}>
        {items.map((item) => (
          <a
            key={item.id}
            href={item.href}
            aria-current={item.id === value ? 'page' : undefined}
            className={tabClass(item.id === value)}
          >
            <TabContent item={item} />
          </a>
        ))}
      </nav>
    );
  }

  const move = (index: number) => {
    const next = (index + items.length) % items.length;
    refs.current[next]?.focus();
    onValueChange?.(items[next].id);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = items.findIndex((i) => i.id === value);
    if (e.key === 'ArrowRight') move(at + 1);
    else if (e.key === 'ArrowLeft') move(at - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(items.length - 1);
    else return;
    e.preventDefault();
  };

  return (
    <div role="tablist" aria-label={label} onKeyDown={onKeyDown} className={rowClass}>
      {items.map((item, index) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`${idPrefix ?? autoPrefix}-tab-${item.id}`}
            aria-controls={idPrefix ? `${idPrefix}-panel-${item.id}` : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onValueChange?.(item.id)}
            className={tabClass(selected)}
          >
            <TabContent item={item} />
          </button>
        );
      })}
    </div>
  );
}
